/**
 * The outcome of one charge.
 *
 * Status and verification are one column, not two: verification is only ever a
 * qualifier on an outcome, and before a run there is nothing to qualify. The
 * cell renders nothing at all until a run has started, so a table nobody has
 * charged yet does not read as a wall of warnings.
 *
 * A success means the payment was taken: core confirms the Stripe charge is
 * paid before returning success. Verification is the further check that it was
 * written back against the ambassador record.
 *
 * `indeterminate` is styled distinctly from both success and clean rejection on
 * purpose: it is the one outcome where the operator has to go and check Stripe.
 */

import React from 'react';
import { Icon, Loader } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import { i18nKeys, rowOutcome } from '../constants';
import { chargeErrorMessage } from '../helpers';
import { SummaryRow } from '../types';
import styles from '../BulkChargeGuarantee.module.css';

interface RowStatusCellProps {
  row: SummaryRow;
}

/**
 * Confirmation deliberately requires `chargedViaDonationPlan`, while
 * `deriveRowEligibility` blocks on that OR a positive
 * `donationPlanChargedAmount`. The asymmetry is intended: the two directions
 * have different costs. Blocking on weaker evidence risks a wasted look at
 * Stripe, confirming on weaker evidence tells the operator money moved when it
 * may not have. Core writes both fields together in
 * `UpdateDonationPlanChargeFields`, so they should never disagree, but if they
 * ever do this errs towards under-claiming.
 */
const VerificationNote: React.FC<{ row: SummaryRow }> = ({ row }) => {
  if (!row.verification) {
    return null;
  }
  if (row.verification.chargedViaDonationPlan === true) {
    return (
      <span className={styles.verificationConfirmed}>
        <Icon name="check" />
        {I18n.t(i18nKeys.VERIFICATION.CONFIRMED)}
      </span>
    );
  }
  return (
    <span className={styles.verificationPending}>
      {I18n.t(i18nKeys.VERIFICATION.NOT_CONFIRMED)}
    </span>
  );
};

const RowStatusCell: React.FC<RowStatusCellProps> = ({ row }) => {
  const label = I18n.t(i18nKeys.OUTCOME[row.outcome]);

  if (row.outcome === rowOutcome.pending) {
    return null;
  }

  if (row.outcome === rowOutcome.charging) {
    return (
      <span className={`${styles.statusCell} ${styles.statusCharging}`}>
        <Loader active inline size="tiny" />
        {label}
      </span>
    );
  }

  if (row.outcome === rowOutcome.blocked) {
    return <span className={styles.statusRejected}>{label}</span>;
  }

  if (row.outcome === rowOutcome.submitted) {
    return (
      <span>
        <span className={`${styles.statusCell} ${styles.statusSubmitted}`}>
          <Icon name="check circle" />
          {label}
        </span>
        <VerificationNote row={row} />
      </span>
    );
  }

  const isIndeterminate = row.outcome === rowOutcome.indeterminate;
  return (
    <span>
      <span
        className={`${styles.statusCell} ${
          isIndeterminate ? styles.statusIndeterminate : styles.statusRejected
        }`}
      >
        <Icon name={isIndeterminate ? 'warning sign' : 'times circle'} />
        {label}
      </span>
      <span className={styles.errorText}>
        {chargeErrorMessage(row.httpStatus, row.errorCode)}
      </span>
      <VerificationNote row={row} />
    </span>
  );
};

export default RowStatusCell;
